/**
 * PuzzleRoyaleTournamentScreen (P8)
 *
 * The durable, state-aware DEEP-LINK target for a single Puzzle Royale
 * tournament: web route `puzzle-royale-tournament/:tournamentId`. It is the
 * ONE public link for a tournament across its whole lifecycle — the detail
 * component below decides what to show from the tournament's own state
 * (scheduled → register, live → join/watch, completed → results, cancelled →
 * notice). This is why the link is durable and id-keyed: unlike the ephemeral
 * join `share_code` (which only works while live), the numeric id resolves at
 * any time.
 *
 * IMPORTANT: this is a SEPARATE route from the chess-tournament flow
 * (`CCTournaments?tournamentId=`) — the id spaces are different and must not be
 * crossed (skill.md pitfall 34).
 *
 * The screen is a thin wrapper: it pulls `tournamentId` off the route params
 * (deep-link path param OR navigate() param) and renders
 * PuzzleRoyaleTournamentDetail, which owns all the state-aware behaviour and is
 * also embedded inline by the discovery tab. Back goes to the tournaments page.
 */

import React from 'react';
import {StyleSheet, View} from 'react-native';
import {useNavigation, useRoute} from '@react-navigation/native';

import CCColors from '@/Utils/CCColors';
import {useSocketReconnect} from '@/Hooks/UseSocket';
import ConnectionModal from '@/Screens/PlayChess/components/ConnectionModal';
import ConnectionNotice from './components/ConnectionNotice';
import PuzzleRoyaleTournamentDetail from './components/PuzzleRoyaleTournamentDetail';

export default function PuzzleRoyaleTournamentScreen(props: {
  // Threaded down from Application.tsx like every drawer screen. The navigator
  // ALREADY passes these to every screen — this component just never accepted
  // them, so the socket/auth chrome that PlayChess, the Fun Challenges lobby
  // and the royale battle screen all render was missing here. Registration is a
  // socket send, so on a dead or hijacked socket "Join" silently did nothing
  // with no explanation.
  changeClient?: boolean;
  setChangeClient?: (val: boolean) => void;
  authComplete?: boolean;
}) {
  const {changeClient, setChangeClient, authComplete = true} = props;
  const navigation = useNavigation<any>();
  const reconnectSocket = useSocketReconnect();
  const route = useRoute<any>();
  // `royaleTournamentId` is the CANONICAL param. It is deliberately a DIFFERENT
  // NAME from the chess `tournamentId`: react-navigation merges params across a
  // navigate(), so while both routes used `tournamentId` this screen's ROYALE id
  // rode along to PlayChess (the CCTournaments screen), whose
  // `if (tournamentId) getTournamentDetails(tournamentId)` opened the CHESS
  // tournament of the SAME NUMBER — e.g. royale 40 opened chess tournament 40
  // (skill.md pitfall 34). `tournamentId`/`id` are still read as fallbacks so
  // links minted before the rename keep resolving.
  const tournamentId =
    route?.params?.royaleTournamentId ??
    route?.params?.tournamentId ??
    route?.params?.id ??
    null;

  // No Back affordance. The left nav is always visible so this screen never
  // needed one, and every routing attempt landed somewhere wrong.

  // Multi-tab / auth-gate modal — the same shared ConnectionModal the Fun
  // Challenges lobby and battle screens use, so a socket takeover or a failed
  // auth is explained here instead of leaving a dead "Join" button.
  //
  // No deep-link grace window is needed (unlike FunChallenges/index.tsx): that
  // suppression exists because a `?join=` cold open races the socket auth and
  // would flash "Auth Failed". This route never auto-joins on mount — it just
  // renders tournament detail from REST — so there is no such race.
  const showConnectionModal = !!changeClient || !authComplete;

  return (
    <View style={styles.root}>
      {showConnectionModal && (
        // The modal is `position:absolute; height:100%`, which collapses inside
        // a scroll/flex container — so pin it to the viewport, matching the
        // wrapper the lobby route uses.
        <View
          style={
            {
              position: 'fixed' as any,
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1000,
            } as any
          }>
          <ConnectionModal
            visible
            onContinue={() => {
              setChangeClient?.(false);
              reconnectSocket();
            }}
            onClose={() => navigation.navigate('Dashboard')}
            authFail={!authComplete}
          />
        </View>
      )}
      {/* Socket-level disconnect banner — parity with the lobby route. */}
      <ConnectionNotice />
      <View style={styles.centerColumn}>
        {tournamentId != null ? (
          <PuzzleRoyaleTournamentDetail tournamentId={tournamentId} />
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {flex: 1, minHeight: 0, backgroundColor: '#FAF6EB'},
  centerColumn: {
    flex: 1,
    // minHeight:0 so the shell below can be bounded and its rail scrolls
    // internally instead of inflating this column (see RoyaleLobbyShell).
    minHeight: 0,
    width: '100%',
    // NO maxWidth and NO padding. PuzzleRoyaleBattle renders edge-to-edge and
    // RoyaleLobbyShell supplies its own padding:20, so capping this at 1040px
    // (plus 16px of extra padding) squeezed the same shell into a much narrower
    // box — which is why the tournament's right rail looked tiny next to the
    // live screen's. Removing the cap makes the two genuinely identical.
    alignSelf: 'center',
  },
});
